import { useState, useMemo, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CalendarClock, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMES = ["09:00", "10:30", "13:00", "14:30", "16:00"];

const nextBusinessDays = (n) => {
  const days = [];
  const d = new Date();
  while (days.length < n) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) days.push(new Date(d));
  }
  return days;
};

const fmt = (d) => d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

const SchedulePicker = ({ leadId, onScheduled }) => {
  const days = useMemo(() => nextBusinessDays(10), []);
  const [day, setDay] = useState(null);
  const [time, setTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [taken, setTaken] = useState([]);

  const loadTaken = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/leads/slots/taken`);
      setTaken(data.slots || []);
    } catch {}
  }, []);

  useEffect(() => { loadTaken(); }, [loadTaken]);

  const isTaken = (d, t) => taken.includes(`${fmt(d)} · ${t} PT`);

  const confirm = async () => {
    setLoading(true);
    const slot = `${fmt(day)} · ${time} PT`;
    try {
      await axios.patch(`${API}/leads/demo/${leadId}/schedule`, { slot });
      toast.success("Demo slot confirmed. Check your inbox.");
      onScheduled(slot);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("That slot was just taken. Pick another time.");
        setTime(null);
        loadTaken();
      } else {
        toast.error("Could not book that slot. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="schedule-picker" className="mt-6 border border-zinc-900/10 bg-[#FFFFFF] p-8">
      <div className="flex items-center gap-3">
        <CalendarClock size={18} className="text-cyan-600" />
        <p className="label-mono">Pick your demo slot</p>
      </div>
      <p className="mt-3 text-sm text-zinc-500">30 minutes, Pacific Time. Skip if you'd rather we propose times.</p>

      <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Date</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {days.map((d) => (
          <button
            key={d.toISOString()}
            data-testid={`slot-day-${d.getDate()}`}
            onClick={() => setDay(d)}
            className={`border px-4 py-2.5 font-mono text-xs transition-colors duration-200 ${
              day?.toDateString() === d.toDateString() ? "border-cyan-500 bg-cyan-600 text-white" : "border-zinc-900/20 text-zinc-600 hover:border-zinc-900/40 hover:text-zinc-900"
            }`}
          >
            {fmt(d)}
          </button>
        ))}
      </div>

      {day && (
        <>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Time (PT)</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {TIMES.filter((t) => !isTaken(day, t)).map((t) => (
              <button
                key={t}
                data-testid={`slot-time-${t.replace(":", "")}`}
                onClick={() => setTime(t)}
                className={`border px-5 py-2.5 font-mono text-xs transition-colors duration-200 ${
                  time === t ? "border-cyan-500 bg-cyan-600 text-white" : "border-zinc-900/20 text-zinc-600 hover:border-zinc-900/40 hover:text-zinc-900"
                }`}
              >
                {t}
              </button>
            ))}
            {TIMES.every((t) => isTaken(day, t)) && (
              <p data-testid="slot-day-full" className="font-mono text-xs text-zinc-500">All slots taken this day — pick another date.</p>
            )}
          </div>
        </>
      )}

      {day && time && (
        <button data-testid="schedule-confirm-btn" onClick={confirm} disabled={loading} className="btn-primary mt-8 w-full">
          {loading ? "Booking…" : <>Confirm {fmt(day)} · {time} PT <Check size={16} /></>}
        </button>
      )}
    </motion.div>
  );
};

export default SchedulePicker;
